import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Siren, FileText } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { EntitySelect } from '@/components/ui/EntitySelect';

interface RecallRow {
  id: string;
  batch_id: string;
  batch_code: string;
  reason: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  issued_at: string;
  closed_at: string | null;
  issued_by_name: string | null;
  affected_kits_json: string | null;
  affected_patients_json: string | null;
  ccih_notified_at: string | null;
}

const STATUS_TONE: Record<RecallRow['status'], 'danger' | 'warning' | 'success'> = {
  OPEN: 'danger',
  IN_PROGRESS: 'warning',
  CLOSED: 'success',
};

function safeLen(json: string | null): number {
  if (!json) return 0;
  try { const a = JSON.parse(json); return Array.isArray(a) ? a.length : 0; } catch { return 0; }
}

export function RecallPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['recall', 'list'],
    queryFn: () => apiGet<{ items: RecallRow[] }>('/v1/recall'),
    refetchInterval: 15_000,
  });

  const [batchId, setBatchId] = useState('');
  const [reason, setReason] = useState('');
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = useMutation({
    mutationFn: () => apiPost<{ id: string; status: string; cascade_queued: boolean }>(
      '/v1/recall/trigger',
      { batch_id: batchId.trim(), reason: reason.trim() },
      { idempotencyKey: `recall-${batchId.trim()}-${Date.now()}` },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recall'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setBatchId(''); setReason(''); setConfirm1(false); setConfirm2(false); setError(null);
    },
    onError: (e) => {
      if (e instanceof ApiError) setError(`${e.code}: ${e.message}`);
      else setError((e as Error).message);
    },
  });

  const canFire = batchId.trim().length > 0 && reason.trim().length >= 10 && confirm1 && confirm2 && !trigger.isPending;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Recall biológico
        </h1>
        <p className="text-sm text-neutral-500">
          Botão de pânico: dispara cascata sobre todos os kits, cirurgias e pacientes vinculados ao lote, e notifica a CCIH.
        </p>
      </header>

      <Card className="border-danger/40">
        <CardHeader
          title={<span className="flex items-center gap-2 text-danger"><Siren size={18} /> Disparar recall</span>}
          subtitle="Confirme duas vezes. A ação é registrada na auditoria e não pode ser desfeita."
        />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <EntitySelect
              label="Lote afetado"
              required
              value={batchId}
              onChange={setBatchId}
              endpoint="/v1/batches"
              displayField="code"
              secondaryField="status"
              placeholder="— Selecione o lote —"
              hint="a lista mostra todos os lotes liberados ou em processo"
            />
            <Input
              label="Motivo (mínimo 10 caracteres)"
              placeholder="Ex.: BI positivo no ciclo 2025-05-28-AC1"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              hint={`${reason.trim().length}/10`}
            />
          </div>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={confirm1} onChange={(e) => setConfirm1(e.target.checked)} className="mt-1" />
              <span>Confirmo que verifiquei o resultado do indicador biológico e há evidência registrada.</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={confirm2} onChange={(e) => setConfirm2(e.target.checked)} className="mt-1" />
              <span>Estou ciente de que a cascata atualizará o status de todos os kits e cirurgias do lote e notificará a CCIH.</span>
            </label>
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <div className="flex justify-end">
            <Button
              variant="danger"
              size="touch"
              leadingIcon={<AlertOctagon size={18} />}
              disabled={!canFire}
              loading={trigger.isPending}
              onClick={() => trigger.mutate()}
            >
              {trigger.isPending ? 'Disparando…' : 'Disparar recall'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Histórico de recalls"
          subtitle={list.isLoading ? 'Carregando…' : `${list.data?.items.length ?? 0} registro(s)`}
        />
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
              <tr>
                <th className="px-4 py-2">Lote</th>
                <th className="px-4 py-2">Motivo</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Kits</th>
                <th className="px-4 py-2">Pacientes</th>
                <th className="px-4 py-2">CCIH</th>
                <th className="px-4 py-2">Quando</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((r) => (
                <tr key={r.id} className="border-t border-neutral-200 dark:border-neutral-700">
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.batch_code}</div>
                    <div className="font-mono text-xxs text-neutral-500">{r.batch_id}</div>
                  </td>
                  <td className="px-4 py-2 text-xs">{r.reason}</td>
                  <td className="px-4 py-2"><Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs">{safeLen(r.affected_kits_json)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{safeLen(r.affected_patients_json)}</td>
                  <td className="px-4 py-2 text-xs">
                    {r.ccih_notified_at ? <Badge tone="success">notificada</Badge> : <Badge tone="neutral">pendente</Badge>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xxs text-neutral-500">{r.issued_at}</td>
                </tr>
              ))}
              {!list.isLoading && (list.data?.items?.length ?? 0) === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-neutral-500">
                  <FileText size={20} className="mx-auto mb-1 text-neutral-400" />
                  Nenhum recall registrado.
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
